import { useEffect, useRef, useState } from "react";
import { useRouter } from "@lib/i18n/navigation-pages-router";
import classNames from "classnames";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { Link } from "@components/core";
import isServer from "@utils/is-server";
import { calculateNextImageSizesByDevice } from "@utils/images/calculateNextImageSizesByDevice";
import catalogContexts from "@utils/constants/catalogContexts";
import ProductImage from "@components/core/ProductImage";

function ProductPicturesCarousel({ algolia, product, onClick, position, context, ...other }) {
  const router = useRouter();
  const asPath = router?.asPath;
  const query = asPath?.split("?")?.[1];

  const windowWidth = typeof window !== "undefined" ? window.innerWidth : 0;
  const deviceSize = windowWidth >= 992 ? "desktop" : "mobile";

  const swiperRef = useRef(null);
  const productPicturesList = product?.slider_pictures || [];

  const [isCarouselAnimated, setIsCarouselAnimated] = useState(false);
  const [firstImageLoaded, setFirstImageLoaded] = useState(false);

  function onLoadingComplete() {
    if (isServer() || deviceSize === "desktop" || position !== 1) return;

    setFirstImageLoaded(true);
  }

  function buildUrlPattern() {
    let productPathName = `/products/${product.slug}`;

    productPathName += `?algoliaQueryId=${algolia?.query_id}&algoliaIndexName=${algolia?.index_name}`;
    if (context === catalogContexts.sales) productPathName += `&context=${catalogContexts.sales}`;

    return productPathName;
  }

  function startAnimation() {
    if (firstImageLoaded && !query) {
      setIsCarouselAnimated(true);

      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.slideTo(1, 1000, true);
        }

        setTimeout(() => {
          if (swiperRef.current) {
            swiperRef.current.slideTo(0, 1000, true);
          }
        }, 500);
      }, 750);
    }
  }

  function handleLinkClick(e) {
    e.preventDefault();

    onClick();
    router.push(buildUrlPattern());
  }

  useEffect(() => {
    startAnimation();
  }, [firstImageLoaded, position, query]);

  return (
    <div className="catalog-product-pictures-carousel-container">
      <Swiper
        className="catalog-product-pictures-carousel"
        lazy="true"
        navigation={true}
        modules={[Autoplay, Navigation]}
        autoplay={isCarouselAnimated}
        onSwiper={(swiper) => (swiperRef.current = swiper)}
      >
        {productPicturesList.map((picture, index) => (
          <SwiperSlide key={`images-${picture}-${index}`}>
            <Link
              href={`/products/${product.slug}`}
              prefetch={false}
              onClick={handleLinkClick}
              data-testid="product-image-link"
              className={classNames(!product?.slug && "product-link-disabled")}
              {...other}
            >
              <ProductImage
                src={picture}
                sizes={calculateNextImageSizesByDevice(deviceSize)}
                onLoad={onLoadingComplete}
                className="catalog-product-pictures-carousel-product-image"
              />
            </Link>
            <div className="swiper-lazy-preloader swiper-lazy-preloader-white"></div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

export default ProductPicturesCarousel;
